import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';

type UseModalReturnType = {
  ref: MutableRefObject<HTMLDialogElement | null>;
  showModal: () => void;
  closeModal: () => void;
};

export const useModal = ({
  onOpen,
  onClose,
  onOutsideClick,
}: {
  onOpen?: (t: UseModalReturnType) => void;
  onClose?: (t: UseModalReturnType) => void;
  onOutsideClick?: (t: UseModalReturnType) => void;
}): UseModalReturnType => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [open, setOpen] = useState(false);

  const showModal = () => {
    if (!ref.current) return;
    ref.current.showModal();
  };

  const closeModal = () => {
    if (!ref.current) return;
    ref.current.close();
  };

  const ret = useMemo(
    () => ({
      ref,
      showModal,
      closeModal,
    }),
    [ref]
  );

  useEffect(() => {
    const opener = () => {
      typeof onOpen === 'function' && onOpen(ret);
      setOpen(true);
    };

    const observerM = new MutationObserver((recs) => {
      recs.forEach(({ attributeName: attr, target }) => {
        if (attr === 'open' && (target as HTMLDialogElement).open) opener();
      });
    });

    ref.current && observerM.observe(ref.current, { attributes: true });

    return () => observerM.disconnect();
  }, [onOpen, ret]);

  useEffect(() => {
    const tRef = ref;
    const closer = () => {
      typeof onClose === 'function' && onClose(ret);
      setOpen(false);
    };
    ref.current?.addEventListener('close', closer);

    return () => tRef.current?.removeEventListener('close', closer);
  }, [onClose, ret]);

  useEffect(() => {
    if (typeof onOutsideClick === 'function' && open) {
      const handleOutsideClick = (e: MouseEvent, cb: () => void) => {
        if (!ref.current || !ref.current.open) return;
        const target = ref.current as HTMLDialogElement;
        if (target.tagName !== 'DIALOG') return;

        const rect = target.getBoundingClientRect();

        const clickedInDialog =
          rect.top <= e.clientY &&
          e.clientY <= rect.top + rect.height &&
          rect.left <= e.clientX &&
          e.clientX <= rect.left + rect.width;

        if (!clickedInDialog) cb();
      };

      document.addEventListener(
        'click',
        (e) => handleOutsideClick(e, () => onOutsideClick(ret)),
        true
      );
      return document.removeEventListener(
        'click',
        (e) => handleOutsideClick(e, () => onOutsideClick(ret)),
        true
      );
    }
  }, [onOutsideClick, open, ret]);

  return ret;
};
